/**
 * `/settings/organization`.
 *
 * ## What changed in the 2026-08-29 sweep
 *
 * The settings were READ-ONLY under a placard that said so ("org.settings.update
 * exists but is read-only here"). It does exist, `apps/api` wires it, and
 * `orgSettingsRepo.update` enforces owner|admin itself — so this screen now
 * writes. Every field the contract accepts and a human can reason about is
 * editable:
 *
 *  - **timezone** — what "07:00" means for the digest;
 *  - **enrichment credit cap** and **auto-enrich on create** — money;
 *  - **share enriched fields** — the legal default is OFF, and the copy says why;
 *  - **recording consent mode** and the **all-party assertion** — the one setting
 *    with a legal consequence, so it is spelled out rather than labelled with the
 *    enum value.
 *
 * `llmMonthlyTokenCapTop` is accepted by the contract and NOT offered: it is a
 * token count on the top model tier, which is a number nobody outside this repo
 * can set meaningfully. Spend is visible on the profile page, and the enrichment
 * cap is the budget control a person actually has an opinion about.
 *
 * ## What is still a placeholder
 *
 * Members and invitations. Those live on better-auth's organization plugin and
 * have no CRM contract procedure — `org.create`, `org.list`, `org.get` and
 * `org.switchActive` are the whole surface, and none of them adds a person.
 */

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Field,
  fieldAria,
  Input,
  SkeletonRows,
  toast,
} from '@crm/ui';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useCrmClient } from '../../../api/client.ts';
import { queryKeys } from '../../../api/keys.ts';
import { useUpdateOrgSettingsMutation } from '../../../api/queries.ts';
import { useOrgId } from '../../../api/session.ts';
import { Planned } from '../../../components/placeholders.tsx';
import { RouteErrorPanel } from '../../../components/route-error-boundary.tsx';
import { toClientError } from '../../../lib/errors.ts';

export const Route = createFileRoute('/_authed/settings/organization')({
  component: OrganizationSettings,
  errorComponent: RouteErrorPanel,
});

type ConsentMode = 'disclose_always' | 'disclose_two_party_only' | 'never_record';

const CONSENT_OPTIONS: readonly { value: ConsentMode; label: string; detail: string }[] = [
  {
    value: 'disclose_always',
    label: 'Always say a recording is running',
    detail: 'The safest option, and the only one that is correct everywhere.',
  },
  {
    value: 'disclose_two_party_only',
    label: 'Say so only where the law requires it',
    detail: 'Needs the assertion below, because a calendar cannot tell where anyone is sitting.',
  },
  {
    value: 'never_record',
    label: 'Never record anything',
    detail: 'The recorder refuses to arm, for everyone in this workspace.',
  },
];

type FormState = {
  timezone: string;
  enrichmentMonthlyCreditCap: string;
  enrichmentAutoOnCreate: boolean;
  allowEnrichedFieldSharing: boolean;
  recordingDefaultConsentMode: ConsentMode;
  recordingAllPartyConsentAssumed: boolean;
};

function Toggle({
  id,
  label,
  hint,
  checked,
  onChange,
}: {
  id: string;
  label: string;
  hint: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <label htmlFor={id} className="flex min-h-11 cursor-pointer items-start gap-3 py-1">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-1 size-4 shrink-0 accent-[var(--color-primary)]"
      />
      <span className="flex flex-col gap-0.5">
        <span className="font-medium">{label}</span>
        <span className="text-xs text-muted-foreground">{hint}</span>
      </span>
    </label>
  );
}

function OrganizationSettings() {
  const client = useCrmClient();
  const orgId = useOrgId();
  const update = useUpdateOrgSettingsMutation();

  const orgs = useQuery({
    queryKey: queryKeys.orgs(orgId),
    queryFn: () => client.org.list({}),
  });

  const settings = useQuery({
    queryKey: queryKeys.orgSettings(orgId),
    queryFn: () => client.org.settings.get({ orgId: orgId ?? '' }),
    enabled: orgId !== null,
  });

  const [form, setForm] = useState<FormState | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Seed the form from the server exactly once per loaded settings object. A
  // controlled form seeded on every render would fight the user's typing.
  useEffect(() => {
    const data = settings.data;
    if (data === undefined) return;
    setForm({
      timezone: data.timezone,
      enrichmentMonthlyCreditCap: String(data.enrichmentMonthlyCreditCap),
      enrichmentAutoOnCreate: data.enrichmentAutoOnCreate,
      allowEnrichedFieldSharing: data.allowEnrichedFieldSharing,
      recordingDefaultConsentMode: data.recordingDefaultConsentMode as ConsentMode,
      recordingAllPartyConsentAssumed: data.recordingAllPartyConsentAssumed,
    });
  }, [settings.data]);

  const patch = (next: Partial<FormState>) =>
    setForm((current) => (current === null ? current : { ...current, ...next }));

  const save = async () => {
    if (form === null || orgId === null) return;
    setError(null);
    const cap = Number.parseInt(form.enrichmentMonthlyCreditCap, 10);
    if (!Number.isFinite(cap) || cap < 0) {
      setError('The credit cap has to be a whole number, zero or more.');
      return;
    }
    try {
      await update.mutateAsync({
        orgId,
        timezone: form.timezone.trim(),
        enrichmentMonthlyCreditCap: cap,
        enrichmentAutoOnCreate: form.enrichmentAutoOnCreate,
        allowEnrichedFieldSharing: form.allowEnrichedFieldSharing,
        recordingDefaultConsentMode: form.recordingDefaultConsentMode,
        recordingAllPartyConsentAssumed: form.recordingAllPartyConsentAssumed,
      });
      toast.success('Workspace settings saved');
    } catch (caught) {
      setError(toClientError(caught).message);
    }
  };

  return (
    <div className="flex max-w-2xl flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>Organizations</CardTitle>
        </CardHeader>
        <CardContent>
          {orgs.isLoading ? (
            <SkeletonRows rows={2} />
          ) : orgs.isError ? (
            <p className="text-sm text-muted-foreground">Could not load organizations.</p>
          ) : (
            <ul className="flex flex-col gap-2 text-sm">
              {(orgs.data?.items ?? []).map((org) => (
                <li key={org.id} className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{org.name}</span>
                  <Badge variant="muted">{org.role}</Badge>
                  {org.isActive ? <Badge variant="secondary">active</Badge> : null}
                  <span className="ml-auto text-xs text-muted-foreground">
                    {org.memberCount} member(s)
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Workspace settings</CardTitle>
        </CardHeader>
        <CardContent className="text-sm">
          {settings.isLoading || form === null ? (
            <SkeletonRows rows={4} />
          ) : settings.isError ? (
            <p className="text-muted-foreground">Could not load these settings.</p>
          ) : (
            <div className="flex flex-col gap-4">
              <Field
                id="org-timezone"
                label="Timezone"
                hint="What “this morning” means for your digest. An IANA name, like Europe/London."
              >
                <Input
                  {...fieldAria('org-timezone', { hint: true })}
                  value={form.timezone}
                  onChange={(event) => patch({ timezone: event.target.value })}
                  placeholder="America/Los_Angeles"
                />
              </Field>

              <Field
                id="org-credit-cap"
                label="Enrichment credits per month"
                hint="A hard ceiling on what looking people up can cost. Zero switches enrichment off."
              >
                <Input
                  {...fieldAria('org-credit-cap', { hint: true })}
                  type="number"
                  min={0}
                  inputMode="numeric"
                  value={form.enrichmentMonthlyCreditCap}
                  onChange={(event) => patch({ enrichmentMonthlyCreditCap: event.target.value })}
                />
              </Field>

              <div className="flex flex-col divide-y divide-border">
                <Toggle
                  id="org-auto-enrich"
                  label="Look people up automatically when they are added"
                  hint="Spends credits without asking. Leave off to decide contact by contact."
                  checked={form.enrichmentAutoOnCreate}
                  onChange={(next) => patch({ enrichmentAutoOnCreate: next })}
                />
                <Toggle
                  id="org-share-enriched"
                  label="Let looked-up details cross a share"
                  hint="Off by default on purpose: the data you buy is licensed to this workspace, not to the people you share with."
                  checked={form.allowEnrichedFieldSharing}
                  onChange={(next) => patch({ allowEnrichedFieldSharing: next })}
                />
              </div>

              <fieldset className="flex flex-col gap-2 border-0 p-0">
                <legend className="mb-1 font-medium">Recording</legend>
                {CONSENT_OPTIONS.map((option) => (
                  <label
                    key={option.value}
                    htmlFor={`consent-${option.value}`}
                    className="flex min-h-11 cursor-pointer items-start gap-3 py-1"
                  >
                    <input
                      id={`consent-${option.value}`}
                      type="radio"
                      name="recording-consent"
                      value={option.value}
                      checked={form.recordingDefaultConsentMode === option.value}
                      onChange={() => patch({ recordingDefaultConsentMode: option.value })}
                      className="mt-1 size-4 shrink-0 accent-[var(--color-primary)]"
                    />
                    <span className="flex flex-col gap-0.5">
                      <span>{option.label}</span>
                      <span className="text-xs text-muted-foreground">{option.detail}</span>
                    </span>
                  </label>
                ))}
                {form.recordingDefaultConsentMode === 'disclose_two_party_only' ? (
                  <Toggle
                    id="org-all-party"
                    label="My meetings are all-party-consent"
                    hint="Tick this only if you know it is true where you and the people you meet are. California is all-party."
                    checked={form.recordingAllPartyConsentAssumed}
                    onChange={(next) => patch({ recordingAllPartyConsentAssumed: next })}
                  />
                ) : null}
              </fieldset>

              {error === null ? null : (
                <p role="alert" className="text-sm font-medium text-destructive">
                  {error}
                </p>
              )}

              <Button className="w-fit" disabled={update.isPending} onClick={() => void save()}>
                {update.isPending ? 'Saving…' : 'Save settings'}
              </Button>
              <p className="text-xs text-muted-foreground">
                Only an owner or an admin of this workspace can change these.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <Planned
        title="Members and invitations"
        description="Inviting a teammate into this workspace, and changing who can do what, will live here. For now a workspace is provisioned for you — sharing individual contacts and lists with someone else's workspace already works from Settings → Sharing."
      />
    </div>
  );
}
